import { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Shield, Zap, Users, CheckCircle, ArrowRight, Share2, Copy } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DetectorScore = ({ name, score }) => {
  const radius = 30;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const isHuman = score < 15;
  const color = isHuman ? '#059669' : '#E11D48';

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="detector-score w-20 h-20">
        <svg width="80" height="80">
          <circle
            cx="40"
            cy="40"
            r={radius}
            stroke="#E4E4E7"
            strokeWidth="6"
            fill="none"
          />
          <circle
            cx="40"
            cy="40"
            r={radius}
            stroke={color}
            strokeWidth="6"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
          />
        </svg>
        <div className="detector-score-text text-sm font-bold">{score}%</div>
      </div>
      <span className="text-xs font-medium text-muted-foreground">{name}</span>
    </div>
  );
};

const Home = () => {
  const { user, login } = useAuth();
  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');
  const [style, setStyle] = useState('academic');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const wordCount = inputText.trim().split(/\s+/).filter(w => w).length;

  const handleHumanize = async () => {
    if (inputText.trim().length < 50) {
      toast.error('Text must be at least 50 characters');
      return;
    }

    setLoading(true);
    setOutputText('');
    setResult(null);

    try {
      const response = await axios.post(
        `${API}/humanize/process`,
        { text: inputText, style },
        { withCredentials: true }
      );

      setOutputText(response.data.output_text);
      setResult(response.data);
      toast.success('Text humanized successfully!');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail || 'Word limit exceeded');
      } else {
        toast.error('Error processing text. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const shareResult = () => {
    if (!result) return;

    const shareData = {
      title: 'Check out my humanized text - Penocio',
      text: 'I used Penocio to make my AI text undetectable!',
      url: `${window.location.origin}/r/${result.result_id}`
    };

    if (navigator.share && navigator.canShare(shareData)) {
      navigator.share(shareData);
    } else {
      copyToClipboard(`${window.location.origin}/r/${result.result_id}`);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Hero Section with Live Editor */}
      <section className="py-16 md:py-24" style={{
        backgroundImage: 'url(https://images.pexels.com/photos/1029624/pexels-photo-1029624.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundBlendMode: 'overlay',
        backgroundColor: 'rgba(250, 250, 250, 0.95)'
      }}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4" variant="secondary">🔥 Used by 50,000+ students & writers</Badge>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-heading font-black tracking-tighter mb-6">
              Make AI text feel like<br />you wrote it
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Where AI writing becomes real. Authentic, detector-safe content in seconds.
            </p>
          </div>

          {/* Live Editor */}
          <Card className="p-6 md:p-8 max-w-6xl mx-auto border border-zinc-200" data-testid="live-editor-card">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Input Side */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading font-bold text-lg">AI-Generated Text</h3>
                  <span className="text-sm text-muted-foreground font-mono">{wordCount} words</span>
                </div>
                <Textarea
                  data-testid="input-textarea"
                  placeholder="Paste your AI-generated text here (minimum 50 characters)..."
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  className="min-h-[300px] font-sans resize-none border-zinc-200 focus:ring-2 focus:ring-black"
                />
                {result && (
                  <div>
                    <p className="text-sm font-medium mb-2">Before Humanization:</p>
                    <div className="flex flex-wrap gap-4">
                      {result.input_detectors.map((detector) => (
                        <DetectorScore key={detector.name} name={detector.name} score={detector.score} />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Output Side */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading font-bold text-lg">Humanized Output</h3>
                  {outputText && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyToClipboard(outputText)}
                        data-testid="copy-output-btn"
                      >
                        <Copy className="h-4 w-4 mr-1" />
                        Copy
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={shareResult}
                        data-testid="share-result-btn"
                      >
                        <Share2 className="h-4 w-4 mr-1" />
                        Share
                      </Button>
                    </div>
                  )}
                </div>
                <div className="min-h-[300px] p-4 border border-zinc-200 rounded-md bg-zinc-50 overflow-y-auto">
                  {loading ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                        <p className="text-sm text-muted-foreground">Humanizing your text...</p>
                      </div>
                    </div>
                  ) : outputText ? (
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{outputText}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">Your humanized text will appear here...</p>
                  )}
                </div>
                {result && outputText && (
                  <div>
                    <p className="text-sm font-medium mb-2 text-accent">After Humanization:</p>
                    <div className="flex flex-wrap gap-4">
                      {result.output_detectors.map((detector) => (
                        <DetectorScore key={detector.name} name={detector.name} score={detector.score} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Controls */}
            <div className="mt-6 flex flex-col sm:flex-row gap-4 items-center">
              <Select value={style} onValueChange={setStyle}>
                <SelectTrigger className="w-full sm:w-48" data-testid="style-selector">
                  <SelectValue placeholder="Select style" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="academic">Academic</SelectItem>
                  <SelectItem value="casual">Casual</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                </SelectContent>
              </Select>

              <Button
                onClick={handleHumanize}
                disabled={loading || wordCount === 0}
                className="w-full sm:flex-1"
                size="lg"
                data-testid="humanize-submit-btn"
              >
                {loading ? 'Processing...' : 'Humanize Text'}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>

              {!user && (
                <Button onClick={login} variant="outline" size="lg" className="w-full sm:w-auto" data-testid="signup-cta-btn">
                  Sign Up for More
                </Button>
              )}
            </div>

            <p className="text-xs text-muted-foreground text-center mt-4">
              {user ? `You have ${user.words_limit - user.word_count_used} words remaining this month` : 'Free users: 2,000 words per request. Sign up for 5,000 words/month.'}
            </p>
          </Card>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-12 bg-secondary">
        <div className="container mx-auto">
          <div className="text-center mb-8">
            <p className="text-sm font-medium text-muted-foreground mb-4">Works with every major detector</p>
            <div className="flex flex-wrap justify-center gap-6 md:gap-12">
              {['Turnitin', 'GPTZero', 'Originality', 'Copyleaks', 'Writer', 'Sapling'].map((detector) => (
                <div key={detector} className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-accent" />
                  <span className="font-medium">{detector}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Preview */}
      <section className="py-24">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="p-8 border-zinc-200">
              <Shield className="h-12 w-12 mb-4 text-primary" />
              <h3 className="font-heading font-bold text-xl mb-2">100% Undetectable</h3>
              <p className="text-muted-foreground leading-relaxed">
                Advanced AI that actually works. Tested against all major detection tools.
              </p>
            </Card>

            <Card className="p-8 border-zinc-200">
              <Zap className="h-12 w-12 mb-4 text-primary" />
              <h3 className="font-heading font-bold text-xl mb-2">Lightning Fast</h3>
              <p className="text-muted-foreground leading-relaxed">
                Get results in seconds, not minutes. Process thousands of words instantly.
              </p>
            </Card>

            <Card className="p-8 border-zinc-200">
              <Users className="h-12 w-12 mb-4 text-primary" />
              <h3 className="font-heading font-bold text-xl mb-2">Trusted by Thousands</h3>
              <p className="text-muted-foreground leading-relaxed">
                Join 50,000+ students, writers, and professionals who use Penocio daily.
              </p>
            </Card>
          </div>

          <div className="text-center mt-12">
            <Link to="/features">
              <Button size="lg" variant="outline">
                See All Features
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary text-primary-foreground">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-heading font-black tracking-tighter mb-6">
            Ready to write with confidence?
          </h2>
          <p className="text-lg md:text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Join thousands of writers who stopped worrying about AI detection.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" onClick={login} data-testid="cta-get-started-btn">
              Get Started Free
            </Button>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                View Pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
